import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import type { RasterAttachImageResult } from '@gik/core/utils/FileUtils';
import { fieldHasError } from '@gik/core/utils/form';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { AttachmentPicker } from '@gik/stream/components/Posts/AttachmentPicker';
import { AttachmentsPreview } from '@gik/stream/components/Posts/AttachmentsPreview';
import type { PostMedia, PostOptions } from '@gik/stream/types/Stream';
import React from 'react';
import ReactDOM from 'react-dom';
import type { SuggestionDataItem } from 'react-mentions';
import useList from 'react-use/lib/useList';
import { Button } from '../../../ui/Button';
import type { FormSchemaEntry } from '../../../ui/Form';
import { Form, FormGroup, validateForm } from '../../../ui/Form';
import { InputMentions } from '../../../ui/Mentions';
import { StreamPostBackgroundPicker } from './StreamPostBackgroundPicker';

export type PostInputPayload = {
  message: string;
  options: PostOptions;
  attachments?: RasterAttachImageResult[];
  attachmentsToRemove?: string[];
};

export type PostInputValues = {
  message: string;
};

export type PostInputProps = {
  inkindRouteId: string;
  backgroundColor?: string;
  attachOnOpen?: boolean;
  buttonsPortal?: React.MutableRefObject<HTMLElement>;
  autoFocus?: boolean;
  value?: string;
  attachmentsEdit?: PostMedia[];
  users?: SuggestionDataItem[];
  onSubmit?: (payload: PostInputPayload, close?: () => void) => void;
  buttonsRef?: React.RefObject<HTMLDivElement>;
} & UIComponent;

const attachmentsLimit = 10;

function PostInputComp({
  children,
  className,
  autoFocus,
  backgroundColor = 'white',
  attachOnOpen = false,
  inkindRouteId,
  buttonsPortal,
  value,
  attachmentsEdit: _attachmentsEdit = [],
  users,
  onSubmit,
  buttonsRef,
  ...otherProps
}: React.PropsWithChildren<PostInputProps>): React.ReactElement {
  const bem = useBemCN('post-input');

  const [bg, setBg] = React.useState<string>(backgroundColor);
  const [open, setOpen] = React.useState<boolean>(bg !== 'white');
  const [attachments, { push: addAttachment, removeAt: removeAttachment }] = useList<RasterAttachImageResult>([]);
  const [attachmentsEdit, { removeAt: removeAttachmentEdit }] = useList<PostMedia>(_attachmentsEdit);
  const [error, setError] = React.useState<string>();

  const maxLength = 5000;

  const schema = React.useMemo<FormSchemaEntry[]>(
    (): FormSchemaEntry[] => [
      {
        name: 'message',
        type: 'text',
        required: attachments?.length === 0 && attachmentsEdit?.length === 0,
        minLength: 1,
        maxLength,
      },
    ],
    [attachments?.length, attachmentsEdit?.length]
  );

  const handleChange = React.useCallback(
    async (values: PostInputValues) => {
      setError(undefined);

      const errors = await validateForm(schema, values);

      if (errors.message && errors.message?.type !== 'required') {
        setError(errors.message?.message);
      }
    },
    [schema]
  );

  const handleSubmit = React.useCallback(
    async (values: PostInputValues) => {
      setError(undefined);

      const attachmentsToRemove: string[] = [];

      _attachmentsEdit.forEach(_media => {
        if (!attachmentsEdit.includes(_media)) {
          attachmentsToRemove.push(_media.id);
        }
      });

      if (!values.message?.trim()) return;

      const payload: PostInputPayload = {
        message: (attachments?.length > 0 || attachmentsEdit?.length > 0) && !values.message ? '' : values.message,
        options: {
          backgroundColor: bg,
        },
        attachments,
        attachmentsToRemove,
      };
      onSubmit?.(payload);
    },
    [_attachmentsEdit, attachmentsEdit, bg, attachments, onSubmit]
  );

  const handleBackgroundSelect = React.useCallback((name: string) => {
    setBg(name);
    setOpen(false);
  }, []);

  const handleSubmitAttempt = React.useCallback(
    async values => {
      // setError(undefined);
      const errors = await validateForm(schema, values);

      if (errors.message?.type === 'required') {
        setError(errors.message?.message);
      }
    },
    [schema]
  );

  // React.useEffect(() => {
  //   if (autoFocus) {
  //     const inputEl = document.getElementById(`newpost-input`) as HTMLTextAreaElement;
  //     if (inputEl) moveCursorToEnd(inputEl);
  //   }
  // }, [autoFocus]);

  const formId = 'postInputForm';
  const styled = bg !== 'white' && bg;

  return (
    <div {...bem(null, [{ [bg]: bg }, { styled }], className)} {...otherProps}>
      <Form
        onSubmit={(values: unknown) => handleSubmit(values as PostInputValues)}
        onSubmitAttempt={handleSubmitAttempt}
        onChange={handleChange}
        schema={schema}
        initialValues={{
          message: value,
        }}
        vertical
        id={formId}
        restoreAfterUpdate
        render={form => {
          const { isSubmitting, isValid, values } = form;
          const buttons = (
            <Button type="submit" fullWidth loading={isSubmitting} disabled={!isValid} form={formId}>
              {value ? 'Save' : 'Post'}
            </Button>
          );

          const toolbar = (
            <div {...bem('toolbar', [{ 'bg-picker-open': open }])}>
              <StreamPostBackgroundPicker
                open={open}
                value={bg}
                onOpenChange={setOpen}
                onSelect={handleBackgroundSelect}
              />

              <AttachmentPicker
                {...bem('attachment-picker')}
                attachImageButtonVisible
                attachImageButtonDisabled={attachments.length >= attachmentsLimit}
                onUploadAttachment={addAttachment}
                attachOnOpen={attachOnOpen}
              />
            </div>
          );

          const toolbarAndButtons = (
            <>
              {toolbar}
              {buttons}
            </>
          );

          return (
            <>
              <div {...bem('input')}>
                <FormGroup showError={false} {...fieldHasError(form, 'message')} vertical>
                  <InputMentions
                    id="newpost-input"
                    placeholder="A kind word or a note of support..."
                    users={users}
                    value={values.message}
                    onChange={event => {
                      form.setFieldValue('message', event.target.value, true);
                    }}
                    autoFocus
                  />

                  {error && <div {...bem('error', null, 'gik-form-error')}>{error}</div>}

                  {/* {input.value.length === 0 && (
                          <>
                            <span {...bem('text-help', null, ['gik-hidden-md-down'])}>
                              or just click POST if there are no words.
                            </span>

                            <span {...bem('text-help', null, ['gik-hidden-md'])}>
                              or just tap POST if there are no words.
                            </span>
                          </>
                        )} */}
                </FormGroup>

                {/* {renderPortal(buttons, () => buttonsPortal.current)} */}
              </div>
              <div {...bem('attachments')}>
                {attachments?.length > 0 && (
                  <AttachmentsPreview attachments={attachments} removeAttachment={removeAttachment} />
                )}
                {attachmentsEdit?.length > 0 && (
                  <AttachmentsPreview attachments={attachmentsEdit} removeAttachment={removeAttachmentEdit} />
                )}
              </div>

              {buttonsRef?.current ? ReactDOM.createPortal(toolbarAndButtons, buttonsRef.current) : toolbarAndButtons}
            </>
          );
        }}
      />
    </div>
  );
}

export const PostInput = withComponentErrorBoundary(PostInputComp);
